/**
 * Utility functions for postal code validation based on country
 */
import {getLocale} from '../intl';

/**
 * Get regex pattern for postal code validation based on country code
 * @param {string} countryCode - ISO country code
 * @returns {Object} - Object containing regex pattern and error message
 */
export function getPostalCodeRegexForCountry (countryCode) {
    // Default pattern and message
    let pattern;

    // Get current locale
    const locale = getLocale();

    // Country-specific patterns
    switch (countryCode) {
        case 'DE': // Germany
            pattern = '^\\d{5}$';
            break;
        case 'AT': // Austria
            pattern = '^\\d{4}$';
            break;
        case 'CH': // Switzerland
            pattern = '^\\d{4}$';
            break;
        case 'FR': // France
            pattern = '^\\d{5}$';
            break;
        case 'IT': // Italy
            pattern = '^\\d{5}$';
            break;
        case 'ES': // Spain
            pattern = '^\\d{5}$';
            break;
        case 'GB': // United Kingdom
            // UK postcodes follow a complex pattern: AA9A 9AA, A9A 9AA, A9 9AA, A99 9AA, AA9 9AA, AA99 9AA
            pattern = '^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$';
            break;
        case 'US': // United States
            // US ZIP codes: 5 digits or 5+4 digits
            pattern = '^\\d{5}(-\\d{4})?$';
            break;
        case 'CA': // Canada
            // Canadian postal codes: A9A 9A9
            pattern = '^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$';
            break;
        case 'NL': // Netherlands
            // Dutch postal codes: 1234 AB
            pattern = '^\\d{4} ?[A-Z]{2}$';
            break;
        case 'BE': // Belgium
            pattern = '^\\d{4}$';
            break;
        case 'DK': // Denmark
            pattern = '^\\d{4}$';
            break;
        case 'SE': // Sweden
            pattern = '^\\d{3} ?\\d{2}$';
            break;
        case 'NO': // Norway
            pattern = '^\\d{4}$';
            break;
        case 'FI': // Finland
            pattern = '^\\d{5}$';
            break;
        case 'PT': // Portugal
            pattern = '^\\d{4}-\\d{3}$';
            break;
        case 'GR': // Greece
            pattern = '^\\d{3} ?\\d{2}$';
            break;
        case 'PL': // Poland
            pattern = '^\\d{2}-\\d{3}$';
            break;
        case 'CZ': // Czech Republic
            pattern = '^\\d{3} ?\\d{2}$';
            break;
        case 'SK': // Slovakia
            pattern = '^\\d{3} ?\\d{2}$';
            break;
        case 'HU': // Hungary
            pattern = '^\\d{4}$';
            break;
        case 'RO': // Romania
            pattern = '^\\d{6}$';
            break;
        case 'BG': // Bulgaria
            pattern = '^\\d{4}$';
            break;
        case 'HR': // Croatia
            pattern = '^\\d{5}$';
            break;
        case 'SI': // Slovenia
            pattern = '^\\d{4}$';
            break;
        case 'LT': // Lithuania
            pattern = '^\\d{5}$';
            break;
        case 'LV': // Latvia
            pattern = '^\\d{4}$';
            break;
        case 'EE': // Estonia
            pattern = '^\\d{5}$';
            break;
        default:
            // For other countries, accept any non-empty value
            pattern = '^.+$';
    }

    // Set message based on locale and country code
    const message = locale === 'de' ? 'Ungültige PLZ' : 'Invalid postal code';

    return {
        pattern,
        message
    };
}

/**
 * Update postal code validator based on country
 * @param {Object} postalCodeInput - Postal code input model
 * @param {string} countryCode - ISO country code
 */
export function updatePostalCodeValidator (postalCodeInput, countryCode) {
    if (!postalCodeInput || !postalCodeInput.validators) {
        console.warn('Postal code input or validators not found');
        return;
    }

    const {
        pattern,
        message
    } = getPostalCodeRegexForCountry(countryCode);

    // Find existing regex validator or create a new one
    const regexValidator = postalCodeInput.validators.find(
        validator => validator.get('type') === 'RegularExpression'
    );

    if (regexValidator) {
        // Update existing validator
        regexValidator.set('regexPattern', pattern);
        regexValidator.set('warning', message);
    } else {
        // Create a new validator if none exists
        // Note: This should be handled by the API, but we're adding it as a fallback
        console.warn('No regex validator found for postal code input, validation might not work as expected');
    }
}
